import { DEMO_TOKENS, DEMO_USER } from './config.js';
import {
  DEMO_DRIVERS_TABLE,
  DEMO_FARES,
  DEMO_PAYOUTS,
  DEMO_RIDERS,
  DEMO_SURGE_ZONES,
  DEMO_TRIPS_TABLE,
} from './data.js';

interface MockOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

function delay<T>(value: T, ms = 220): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

const driverState = DEMO_DRIVERS_TABLE.map((d) => ({ ...d }));
const fareState = DEMO_FARES.map((f) => ({ ...f }));
const surgeState = DEMO_SURGE_ZONES.map((s) => ({ ...s }));
const payoutState = DEMO_PAYOUTS.map((p) => ({ ...p }));

function buildOverview() {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const activeTrips = DEMO_TRIPS_TABLE.filter((t) =>
    ['searching', 'assigned', 'arriving', 'arrived', 'in_progress'].includes(t.status),
  ).length;
  const onlineDrivers = driverState.filter((d) => d.status === 'online' || d.status === 'on_trip').length;
  const todayTrips = DEMO_TRIPS_TABLE.filter((t) => new Date(t.requestedAt) >= startOfToday).length;
  const todayRevenue = DEMO_TRIPS_TABLE.filter(
    (t) => t.status === 'completed' && new Date(t.requestedAt) >= startOfToday,
  ).reduce((acc, t) => acc + t.fareTotal, 0);

  const hourCounts = new Map<number, number>();
  for (const trip of DEMO_TRIPS_TABLE) {
    if (new Date(trip.requestedAt) >= startOfToday) {
      const hour = new Date(trip.requestedAt).getHours();
      hourCounts.set(hour, (hourCounts.get(hour) ?? 0) + 1);
    }
  }
  const tripsByHour = [...hourCounts.entries()].sort(([a], [b]) => a - b).map(([hour, count]) => ({ hour, count }));

  return { activeTrips, onlineDrivers, todayTrips, todayRevenue, tripsByHour };
}

function buildLiveSnapshot() {
  const drivers = driverState
    .filter((d) => (d.status === 'online' || d.status === 'on_trip') && d.lat != null && d.lng != null)
    .map((d) => ({
      id: d.id,
      name: d.user.name,
      status: d.status,
      position: { lat: d.lat as number, lng: d.lng as number },
    }));
  const trips = DEMO_TRIPS_TABLE.filter((t) =>
    ['searching', 'assigned', 'arriving', 'arrived', 'in_progress'].includes(t.status),
  ).map((t) => ({
    id: t.id,
    status: t.status,
    rider: t.rider.name,
    driver: t.driver?.user.name ?? null,
    pickup: { lat: 24.86, lng: 67.01, label: t.pickupLabel },
    drop: { lat: 24.9, lng: 67.08, label: t.dropLabel },
    fareTotal: t.fareTotal,
  }));
  return { drivers, trips };
}

export async function mockRequest<T>(path: string, opts: MockOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const route = new URL(path, 'http://demo.local').pathname;
  const body = (opts.body as Record<string, unknown>) ?? {};

  if (route === '/auth/login') return delay({ user: DEMO_USER, ...DEMO_TOKENS } as unknown as T);
  if (route === '/auth/refresh') return delay(DEMO_TOKENS as unknown as T);
  if (route === '/auth/logout') return delay({ ok: true } as unknown as T);
  if (route === '/auth/me') return delay(DEMO_USER as unknown as T);

  if (route === '/admin/overview' && method === 'GET') return delay(buildOverview() as unknown as T);
  if (route === '/admin/live' && method === 'GET') return delay(buildLiveSnapshot() as unknown as T);

  if (route === '/admin/drivers' && method === 'GET') return delay(driverState as unknown as T);

  const driverMatch = /^\/admin\/drivers\/([^/]+)$/.exec(route);
  if (driverMatch && method === 'PATCH') {
    const id = driverMatch[1]!;
    const driver = driverState.find((d) => d.id === id);
    if (!driver) throw new Error('Driver not found.');
    driver.approved = Boolean(body.approved);
    return delay({ id: driver.id, approved: driver.approved, status: driver.status } as unknown as T);
  }

  if (route === '/admin/riders' && method === 'GET') return delay(DEMO_RIDERS as unknown as T);
  if (route === '/admin/trips' && method === 'GET') return delay(DEMO_TRIPS_TABLE as unknown as T);

  if (route === '/admin/pricing' && method === 'GET') {
    return delay({ fares: fareState, surge: surgeState } as unknown as T);
  }
  if (route === '/admin/pricing/fare' && method === 'PUT') {
    const next = body as { vehicleType: string; base?: number; perKm?: number; perMin?: number; minFare?: number };
    const fare = fareState.find((f) => f.vehicleType === next.vehicleType);
    if (!fare) throw new Error('Fare not found.');
    if (typeof next.base === 'number') fare.base = next.base;
    if (typeof next.perKm === 'number') fare.perKm = next.perKm;
    if (typeof next.perMin === 'number') fare.perMin = next.perMin;
    if (typeof next.minFare === 'number') fare.minFare = next.minFare;
    return delay(fare as unknown as T);
  }
  if (route === '/admin/pricing/surge' && method === 'PUT') {
    const next = body as { id?: string; name: string; multiplier: number; active: boolean };
    if (next.id) {
      const zone = surgeState.find((s) => s.id === next.id);
      if (zone) {
        zone.name = next.name;
        zone.multiplier = next.multiplier;
        zone.active = next.active;
        return delay(zone as unknown as T);
      }
    }
    const created = { id: `s-${Date.now()}`, name: next.name, multiplier: next.multiplier, active: next.active };
    surgeState.push(created);
    return delay(created as unknown as T);
  }

  if (route === '/admin/payouts' && method === 'GET') return delay(payoutState as unknown as T);
  const payoutMatch = /^\/admin\/payouts\/([^/]+)\/paid$/.exec(route);
  if (payoutMatch && method === 'POST') {
    const payout = payoutState.find((p) => p.id === payoutMatch[1]);
    if (!payout) throw new Error('Payout not found.');
    payout.status = 'paid';
    return delay({ id: payout.id, status: payout.status } as unknown as T);
  }

  throw new Error(`Demo mode: route not mocked yet (${method} ${route}).`);
}
